import React, { useState } from 'react';
import { 
  FileText, 
  Lightbulb, 
  Code, 
  Settings, 
  Eye,
  Save,
  AlertCircle,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import { DocumentationTab } from './DocumentationTab';
import { UseCasesTab } from './UseCasesTab';
import { CodeExamplesTab } from './CodeExamplesTab';
import { CompletenessBar } from './CompletenessBar';
import { VisibilityBadge, VisibilityConfirmationDialog } from './VisibilityComponents';
import { useFormDraft } from '../../hooks/useAutoSave';
import { useThemeStore } from '../../stores/theme.store';

interface CodeExample {
  id: string;
  language: 'typescript' | 'python' | 'curl' | 'javascript' | 'java' | 'csharp' | 'go' | 'php';
  title: string;
  description: string;
  code: string;
  operation?: string;
}

interface UseCase {
  id: string;
  title: string;
  description: string;
  businessScenario: string;
  recommendedFor: string[];
  icon?: string;
}

interface ChangelogEntry {
  version: string;
  date: string;
  changes: string[];
}

interface FaqEntry {
  question: string;
  answer: string;
}

interface EnvironmentRequirement {
  environment: 'sandbox' | 'production';
  requirements: string[];
  notes?: string;
}

interface TemplateFormData {
  description: string;
  documentation: string;
  documentationUrl: string;
  setupGuide: string;
  codeExamples: CodeExample[];
  useCases: UseCase[];
  changelog: ChangelogEntry[];
  faq: FaqEntry[];
  knownLimitations: string[];
  environmentRequirements: EnvironmentRequirement[];
  complementaryTemplates: string[];
}

interface TemplateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: {
    id: string;
    name: string;
    isPublic: boolean;
    operations?: Record<string, any>;
  } & Partial<TemplateFormData>;
  completeness?: {
    score: number;
    missingFields: string[];
    suggestions: string[];
  };
  availableTemplates?: { id: string; name: string; description?: string }[];
  onSave: (data: Partial<TemplateFormData>) => Promise<void>;
  onToggleVisibility: () => Promise<void>;
}

type TabId = 'documentation' | 'useCases' | 'codeExamples' | 'settings';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'documentation', label: 'Documentação', icon: <FileText className="w-4 h-4" /> },
  { id: 'useCases', label: 'Casos de Uso', icon: <Lightbulb className="w-4 h-4" /> },
  { id: 'codeExamples', label: 'Exemplos de Código', icon: <Code className="w-4 h-4" /> },
  { id: 'settings', label: 'Configurações', icon: <Settings className="w-4 h-4" /> },
];

export function TemplateEditModal({
  isOpen,
  onClose,
  template,
  completeness,
  availableTemplates = [],
  onSave,
  onToggleVisibility,
}: TemplateEditModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('documentation');
  const [isSaving, setIsSaving] = useState(false);
  const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);
  const { isDarkMode } = useThemeStore();

  // Theme-aware colors
  const colors = isDarkMode ? {
    modalBg: 'bg-gray-900',
    headerBorder: 'border-gray-700',
    titleColor: 'text-white',
    closeButtonColor: 'text-gray-400 hover:text-gray-200 hover:bg-gray-800',
    tabsBorder: 'border-gray-700',
    tabActive: 'bg-blue-900/50 text-blue-400',
    tabInactive: 'text-gray-400 hover:bg-gray-800',
    contentBg: 'bg-gray-900',
    footerBg: 'bg-gray-800',
    footerBorder: 'border-gray-700',
    settingsBg: 'bg-gray-800',
    settingsTitle: 'text-white',
    settingsText: 'text-gray-300',
    unsavedText: 'text-yellow-400',
    savedText: 'text-gray-400',
  } : {
    modalBg: 'bg-white',
    headerBorder: 'border-gray-200',
    titleColor: 'text-gray-900',
    closeButtonColor: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
    tabsBorder: 'border-gray-200',
    tabActive: 'bg-blue-100 text-blue-700',
    tabInactive: 'text-gray-600 hover:bg-gray-100',
    contentBg: 'bg-white',
    footerBg: 'bg-gray-50',
    footerBorder: 'border-gray-200',
    settingsBg: 'bg-gray-50',
    settingsTitle: 'text-gray-900',
    settingsText: 'text-gray-600',
    unsavedText: 'text-yellow-600',
    savedText: 'text-gray-500',
  };

  // Initialize form data with template values
  const initialData: TemplateFormData = {
    description: template.description || '',
    documentation: template.documentation || '',
    documentationUrl: template.documentationUrl || '',
    setupGuide: template.setupGuide || '',
    codeExamples: template.codeExamples || [],
    useCases: template.useCases || [],
    changelog: template.changelog || [],
    faq: template.faq || [],
    knownLimitations: template.knownLimitations || [],
    environmentRequirements: template.environmentRequirements || [],
    complementaryTemplates: template.complementaryTemplates || [],
  };

  const {
    values: formData,
    setField,
    isDirty,
    lastSaved,
    saveNow,
    clearDraft,
  } = useFormDraft(`template-${template.id}`, initialData, {
    interval: 30000,
    enabled: isOpen,
  });

  // Get operations list for code examples
  const operations = template.operations 
    ? Object.keys(template.operations) 
    : [];

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      clearDraft();
    } finally {
      setIsSaving(false);
    }
  };

  // Handle visibility toggle
  const handleVisibilityToggle = async () => {
    await onToggleVisibility();
    setShowVisibilityDialog(false);
  };

  // Handle field change
  const handleFieldChange = (field: keyof TemplateFormData, value: any) => {
    setField(field, value);
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center p-4 z-50 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className={`w-full max-w-6xl ${colors.modalBg} rounded-xl shadow-2xl pointer-events-auto flex flex-col max-h-[90vh]`}
              >
                {/* Header */}
                <div className={`flex items-center justify-between p-6 border-b ${colors.headerBorder} flex-shrink-0`}>
                  <h2 className={`text-xl font-semibold ${colors.titleColor}`}>
                    Editar: {template.name}
                  </h2>
                  <button
                    onClick={onClose}
                    className={`p-2 rounded-lg ${colors.closeButtonColor} transition-colors`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Completeness and Visibility Bar */}
                <div className={`px-6 py-4 border-b ${colors.headerBorder} flex-shrink-0`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <VisibilityBadge 
                        isPublic={template.isPublic} 
                        onClick={() => setShowVisibilityDialog(true)}
                      />
                      {isDirty && (
                        <span className={`text-xs ${colors.unsavedText} flex items-center gap-1`}>
                          <AlertCircle className="w-3 h-3" />
                          Alterações não salvas
                        </span>
                      )}
                      {lastSaved && (
                        <span className={`text-xs ${colors.savedText}`}>
                          Último auto-save: {lastSaved.toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {completeness && (
                    <CompletenessBar
                      score={completeness.score}
                      missingFields={completeness.missingFields}
                      suggestions={completeness.suggestions}
                      onFieldClick={(field) => {
                        if (['Descrição', 'Documentação', 'URL da Documentação', 'Guia de Configuração', 'Changelog', 'FAQ', 'Limitações Conhecidas', 'Requisitos de Ambiente'].includes(field)) {
                          setActiveTab('documentation');
                        } else if (field === 'Casos de Uso' || field === 'Templates Complementares') {
                          setActiveTab('useCases');
                        } else if (field === 'Exemplos de Código') {
                          setActiveTab('codeExamples');
                        }
                      }}
                    />
                  )}
                </div>

                {/* Tabs */}
                <div className={`flex-shrink-0 border-b ${colors.tabsBorder}`}>
                  <nav className="flex gap-1 px-6 py-2">
                    {TABS.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeTab === tab.id
                            ? colors.tabActive
                            : colors.tabInactive
                        }`}
                      >
                        {tab.icon}
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className={`flex-1 overflow-y-auto p-6 ${colors.contentBg}`}>
                  {activeTab === 'documentation' && (
                    <DocumentationTab
                      templateId={template.id}
                      description={formData.description}
                      documentation={formData.documentation}
                      documentationUrl={formData.documentationUrl}
                      setupGuide={formData.setupGuide}
                      changelog={formData.changelog}
                      faq={formData.faq}
                      knownLimitations={formData.knownLimitations}
                      environmentRequirements={formData.environmentRequirements}
                      onChange={(field, value) => handleFieldChange(field as keyof TemplateFormData, value)}
                      isDarkMode={isDarkMode}
                    />
                  )}

                  {activeTab === 'useCases' && (
                    <UseCasesTab
                      useCases={formData.useCases}
                      complementaryTemplates={formData.complementaryTemplates}
                      availableTemplates={availableTemplates.filter(t => t.id !== template.id)}
                      onChange={(field, value) => handleFieldChange(field as keyof TemplateFormData, value)}
                      isDarkMode={isDarkMode}
                    />
                  )}

                  {activeTab === 'codeExamples' && (
                    <CodeExamplesTab
                      codeExamples={formData.codeExamples}
                      operations={operations}
                      onChange={(examples) => handleFieldChange('codeExamples', examples)}
                      isDarkMode={isDarkMode}
                    />
                  )}

                  {activeTab === 'settings' && (
                    <div className="space-y-6">
                      <div className={`${colors.settingsBg} rounded-lg p-6`}>
                        <h3 className={`font-medium ${colors.settingsTitle} mb-4 flex items-center gap-2`}>
                          <Eye className="w-5 h-5" />
                          Visibilidade
                        </h3>
                        <p className={`text-sm ${colors.settingsText} mb-4`}>
                          {template.isPublic
                            ? 'Este template está público e visível para todos os Tenant Admins no marketplace.'
                            : 'Este template está privado e visível apenas para Super Admins.'}
                        </p>
                        <Button
                          variant="secondary"
                          onClick={() => setShowVisibilityDialog(true)}
                        >
                          {template.isPublic ? 'Tornar Privado' : 'Tornar Público'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className={`flex-shrink-0 px-6 py-4 border-t ${colors.footerBorder} flex items-center justify-between ${colors.footerBg} rounded-b-xl`}>
                  <div className="flex items-center gap-2">
                    {isDirty && (
                      <Button variant="ghost" onClick={saveNow} size="sm">
                        <Save className="w-4 h-4 mr-1" />
                        Salvar Rascunho
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" onClick={onClose}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Visibility Confirmation Dialog */}
      <VisibilityConfirmationDialog
        isOpen={showVisibilityDialog}
        onClose={() => setShowVisibilityDialog(false)}
        onConfirm={handleVisibilityToggle}
        currentVisibility={template.isPublic}
        templateName={template.name}
      />
    </>
  );
}

export default TemplateEditModal;
